/* eslint-disable new-cap */
/* eslint-disable promise/param-names */
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

export const onDownload = (url, name, format = '.png') => {
  const link = document.createElement('a')
  link.href = url
  link.download = name.includes(format) ? name : `${name}${format}`
  document.body.appendChild(link)
  link.click()
  URL.revokeObjectURL(url)
  link.remove()
}

/* -------------------------------- Format Chart -------------------------------- */
export const formatChart = ref => {
  const chart = ref?.current?.chartInstance || ref?.current
  if (!chart) return
  const dataUrl = chart.toBase64Image()
  const [, b64] = dataUrl.split(',')
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0))
  return URL.createObjectURL(new Blob([bytes], { type: 'image/png' }))
}

/* -------------------------------- Format SVG -------------------------------- */
export const formatSvg = async ref => {
  const svgEl = ref.current
  if (!svgEl) return

  // 1. clone & serialise the SVG
  const clone = svgEl.cloneNode(true)
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  const svgString = new XMLSerializer().serializeToString(clone)
  const svg64 = btoa(unescape(encodeURIComponent(svgString)))

  // 2. load it into an <img>
  const img = new Image()
  const imgLoaded = new Promise(res => (img.onload = res))
  img.src = `data:image/svg+xml;base64,${svg64}`
  await imgLoaded // wait until the SVG is rendered

  // 3. draw onto a canvas
  const canvas = document.createElement('canvas')
  canvas.width = img.width * 50
  canvas.height = img.height * 50

  const ctx = canvas.getContext('2d')
  ctx.fillStyle = 'transparent'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  // 4. turn canvas → blob‑URL & download
  const blob = await new Promise(res => canvas.toBlob(res, 'image/png'))
  return URL.createObjectURL(blob)
}

/* -------------------------------- Download PDF -------------------------------- */
const svgToPngDataUrl = async svgEl => {
  const clone = svgEl.cloneNode(true)
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  const str = new XMLSerializer().serializeToString(clone)
  const img = new Image()
  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(str)))
  await new Promise(r => (img.onload = r))

  const canvas = document.createElement('canvas')
  canvas.width = 440 * 10
  canvas.height = 620 * 10
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff' // solid background
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/png')
}

export const downloadPDF = async (divRef, name) => {
  // Replace SVG maps with PNGs so the colours won’t disappear
  const svgs = divRef.current.querySelectorAll('svg.taiwan')
  const replacements = []
  for (const svg of svgs) {
    const pngSrc = await svgToPngDataUrl(svg)
    const img = new Image()
    img.src = pngSrc
    img.style.maxWidth = '100%'
    svg.parentNode.replaceChild(img, svg)
    replacements.push([img, svg]) // restore later
  }
  // Hide all elements with class "downloadHide"
  const hiddenEls = [...divRef.current.querySelectorAll('.downloadHide')]
  hiddenEls.forEach(el => {
    el._display = el.style.display // store original inline style
    el.style.display = 'none'
  })

  // Snapshot the entire container once
  const SCALE = 1.5
  const canvas = await html2canvas(divRef.current, {
    scale: SCALE,
    backgroundColor: '#ffffff',
    useCORS: true
  })

  // Locate every page‑break marker (they MUST be display:block)
  const rootTop = divRef.current.getBoundingClientRect().top
  const markers = [...divRef.current.querySelectorAll('.page-break')]

  const breakPx = markers
    .map(el => (el.getBoundingClientRect().top - rootTop) * SCALE)
    .filter(px => px > 0 && px < canvas.height)
    .sort((a, b) => a - b)

  // Helper to crop a slice of the big canvas into its own canvas
  const cropCanvas = (src, y, h) => {
    const slice = document.createElement('canvas')
    slice.width = src.width
    slice.height = h
    slice.getContext('2d').drawImage(src, 0, -y)
    return slice
  }

  // Build the PDF page‑by‑page
  const margin = { top: 14, right: 8, bottom: 14, left: 8 }
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' })

  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const availW = pageW - margin.left - margin.right
  const availH = pageH - margin.top - margin.bottom

  const px2mm = availW / canvas.width
  let yStart = 0
  for (const yStop of [...breakPx, canvas.height]) {
    const slicePx = yStop - yStart
    if (slicePx <= 0) { yStart = yStop; continue }

    const sliceCanvas = cropCanvas(canvas, yStart, slicePx)
    const sliceImg = sliceCanvas.toDataURL('image/png')
    let sliceMmH = slicePx * px2mm

    // if the slice is taller than the remaining space, scale it down
    if (sliceMmH > availH) {
      const shrink = availH / sliceMmH
      sliceMmH = availH
      // redraw width so aspect ratio stays correct
      const drawW = availW * shrink
      const offsetX = margin.left + (availW - drawW) / 2
      pdf.addImage(sliceImg, 'PNG', offsetX, margin.top, drawW, sliceMmH)
    } else {
      pdf.addImage(sliceImg, 'PNG',
        margin.left, margin.top,
        availW, sliceMmH)
    }

    yStart = yStop
    if (yStart < canvas.height) pdf.addPage()
  }

  pdf.save(`${name}.pdf`)

  // Restore modified emelent
  replacements.forEach(([img, svg]) => img.parentNode.replaceChild(svg, img))
  hiddenEls.forEach(el => {
    el.style.display = el._display ?? ''
    delete el._display
  })
}
